import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AI_AGENTS_DATA, getActiveAgents, getTotalTasksCompleted, getAverageSuccessRate, AIAgent } from '../data/agents';
import { useAuth } from '../context/AuthContext';

/**
 * Modern Dashboard Page Component
 * 
 * Ultra-modern dashboard for Agentic AI platform with fluid, non-boxy design
 * 
 * @returns {JSX.Element} Modern dashboard page component
 */
export const ModernDashboard: React.FC = () => {
  const navigate = useNavigate();
  const activeAgents = getActiveAgents();
  const totalTasks = getTotalTasksCompleted();
  const avgSuccessRate = getAverageSuccessRate();
    const {user: authUser} = useAuth();
    const tenant = authUser?.tenant;
  

  const handleAgentClick = (agentId: string) => {
    navigate(`/agents/${agentId}/config`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Colors */}
      <div className="relative overflow-hidden">
        {/* Vibrant Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-agentic-primary-blue via-agentic-primary-blue-light to-agentic-primary-blue-dark">
          <div className="absolute top-0 left-0 w-full h-full">
            {/* Primary Blue Orbs */}
            <div className="absolute top-20 left-10 w-72 h-72 bg-agentic-primary-blue/40 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-96 h-96 bg-agentic-primary-blue-light/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-agentic-primary-blue-dark/35 rounded-full blur-3xl animate-pulse delay-2000"></div>
            
            {/* Accent Color Orbs */}
            <div className="absolute top-32 right-1/4 w-64 h-64 bg-agentic-accent-green/25 rounded-full blur-3xl animate-pulse delay-500"></div>
            <div className="absolute bottom-32 left-1/4 w-56 h-56 bg-agentic-accent-purple/30 rounded-full blur-3xl animate-pulse delay-1500"></div>
            
            {/* Overlay Pattern */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-white/5"></div>
            
            {/* Floating Elements */}
            <div className="absolute top-16 right-16 w-4 h-4 bg-agentic-accent-green rounded-full animate-bounce delay-700"></div>
            <div className="absolute top-24 right-32 w-3 h-3 bg-agentic-accent-purple rounded-full animate-bounce delay-1000"></div>
            <div className="absolute bottom-24 left-16 w-5 h-5 bg-agentic-accent-yellow rounded-full animate-bounce delay-500"></div>
            <div className="absolute bottom-16 left-32 w-2 h-2 bg-agentic-accent-red rounded-full animate-bounce delay-1200"></div>
          </div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 pt-12 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-4 mb-8">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center shadow-2xl border border-white/30">
                <span className="text-white text-3xl">🤖</span>
              </div>
              <div className="text-left">
                <h1 className="text-5xl font-bold text-white mb-2">
                  {tenant?.name} Platform
                </h1>
                <p className="text-white/90 text-xl font-medium">Intelligent Automation Command Center</p>
              </div>
            </div>
            
            {/* Live Status Indicator */}
            <div className="inline-flex items-center space-x-4 px-8 py-4 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/30">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-agentic-accent-green rounded-full animate-pulse"></div>
                <span className="text-sm font-bold text-agentic-text-primary">{activeAgents.length} AI Agents Active</span>
              </div>
              <div className="w-px h-6 bg-agentic-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-agentic-accent-purple rounded-full animate-pulse delay-300"></div>
                <span className="text-sm font-semibold text-agentic-text-secondary">{totalTasks.toLocaleString()} tasks completed today</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fluid Metrics Section */}
      <div className="relative -mt-16 mb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6 lg:gap-8">
          {/* Active AI Agents - Floating Card */}
          <div className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-2xl">🤖</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Active Agents</p>
                  <p className="text-4xl font-bold text-gray-900 mt-1">{activeAgents.length}</p>
                  <div className="flex items-center mt-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-green-600 font-medium">All systems operational</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks Completed - Floating Card */}
          <div className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-2xl">✅</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Tasks Done</p>
                  <p className="text-4xl font-bold text-gray-900 mt-1">{totalTasks.toLocaleString()}</p>
                  <div className="flex items-center mt-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-green-600 font-medium">+12% today</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Processing Time - Floating Card */}
          <div className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-2xl">⚡</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Avg. Speed</p>
                  <p className="text-4xl font-bold text-gray-900 mt-1">2.3s</p>
                  <div className="flex items-center mt-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    <span className="text-sm text-red-600 font-medium">-0.5s faster</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Success Rate - Floating Card */}
          <div className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-2xl">📊</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Success Rate</p>
                  <p className="text-4xl font-bold text-gray-900 mt-1">{avgSuccessRate.toFixed(1)}%</p>
                  <div className="flex items-center mt-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-green-600 font-medium">+0.3% this month</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Analytics Section */}
      <div className="relative mb-16">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-4">
            Performance Analytics
          </h2>
          <p className="text-gray-600 text-lg">Real-time insights and trends</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {/* Task Completion Trend Chart */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Task Completion Trend</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Last 7 days</span>
              </div>
            </div>
            <div className="h-64 relative">
              {/* Simulated Line Chart */}
              <svg className="w-full h-full" viewBox="0 0 400 200">
                <defs>
                  <linearGradient id="taskGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                {/* Grid lines */}
                <g stroke="#e5e7eb" strokeWidth="1" opacity="0.5">
                  <line x1="0" y1="40" x2="400" y2="40"/>
                  <line x1="0" y1="80" x2="400" y2="80"/>
                  <line x1="0" y1="120" x2="400" y2="120"/>
                  <line x1="0" y1="160" x2="400" y2="160"/>
                </g>
                {/* Data line */}
                <path d="M 20 160 Q 80 140 120 120 T 200 80 T 280 60 T 360 40" 
                      stroke="#3b82f6" strokeWidth="3" fill="none"/>
                {/* Area under curve */}
                <path d="M 20 160 Q 80 140 120 120 T 200 80 T 280 60 T 360 40 L 360 200 L 20 200 Z" 
                      fill="url(#taskGradient)"/>
                {/* Data points */}
                <circle cx="20" cy="160" r="4" fill="#3b82f6"/>
                <circle cx="80" cy="140" r="4" fill="#3b82f6"/>
                <circle cx="120" cy="120" r="4" fill="#3b82f6"/>
                <circle cx="200" cy="80" r="4" fill="#3b82f6"/>
                <circle cx="280" cy="60" r="4" fill="#3b82f6"/>
                <circle cx="360" cy="40" r="4" fill="#3b82f6"/>
              </svg>
              <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 px-4">
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
                <span>Sun</span>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-green-600">+12.5%</span> from last week
              </div>
              <div className="text-sm text-gray-600">
                Avg: <span className="font-semibold">1,247 tasks/day</span>
              </div>
            </div>
          </div>

          {/* Agent Performance Distribution */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Agent Performance</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Success Rate %</span>
              </div>
            </div>
            <div className="space-y-4">
              {AI_AGENTS_DATA.slice(0, 5).map((agent, index) => (
                <div key={agent.id} className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-lg">{agent.icon}</span>
                    <span className="text-sm font-medium text-gray-700 truncate">{agent.name}</span>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`bg-gradient-to-r ${agent.color} h-2 rounded-full transition-all duration-1000`}
                        style={{ width: `${agent.successRate}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-12 text-right">{agent.successRate}%</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Average Performance</span>
                <span className="font-semibold text-gray-900">{avgSuccessRate.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Heatmap */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30 mb-12">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Agent Activity Heatmap</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gray-200 rounded"></div>
                <span className="text-xs text-gray-600">Low</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-300 rounded"></div>
                <span className="text-xs text-gray-600">Medium</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-600 rounded"></div>
                <span className="text-xs text-gray-600">High</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, dayIndex) => (
              <div key={day} className="text-center">
                <div className="text-xs text-gray-500 mb-2">{day}</div>
                <div className="space-y-1">
                  {Array.from({ length: 12 }, (_, hourIndex) => {
                    const intensity = Math.random();
                    const bgColor = intensity > 0.7 ? 'bg-blue-600' : intensity > 0.4 ? 'bg-blue-300' : 'bg-gray-200';
                    return (
                      <div key={hourIndex} className={`w-3 h-3 ${bgColor} rounded-sm`}></div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 text-center">
            Activity levels throughout the week (12 hours shown)
          </div>
        </div>
      </div>

      {/* AI Agent Ecosystem - Full Width Layout */}
      {/* <div className="relative mb-16">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-4">
            AI Agent Ecosystem
          </h2>
          <p className="text-gray-600 text-lg">Intelligent agents working in harmony</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
          {AI_AGENTS_DATA.slice(0, 10).map((agent) => (
            <div key={agent.id} className="group relative">
              <div className={`absolute -inset-1 bg-gradient-to-r ${agent.color}/30 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500`}></div>
              <div 
                className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 cursor-pointer"
                onClick={() => handleAgentClick(agent.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <div className={`w-10 h-10 bg-gradient-to-br ${agent.color} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                      <span className="text-white text-lg">{agent.icon}</span>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 truncate leading-tight">{agent.name}</h4>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ml-2 ${
                    agent.status === 'active' 
                      ? 'text-green-700 bg-green-100' 
                      : agent.status === 'inactive'
                      ? 'text-gray-700 bg-gray-100'
                      : 'text-yellow-700 bg-yellow-100'
                  }`}>
                    {agent.status}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Tasks</span>
                    <span className="text-lg font-bold text-gray-900">{agent.tasksCompleted.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Success Rate</span>
                    <span className="text-lg font-bold text-gray-900">{agent.successRate}%</span>
                  </div>
                  
                  {/* Progress Bar
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className={`bg-gradient-to-r ${agent.color} h-2 rounded-full transition-all duration-1000`} style={{ width: `${agent.successRate}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div> */}

      {/* Additional Analytics Charts */}
      <div className="relative mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Response Time Distribution */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Response Time</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Avg: 2.3s</span>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { range: '< 1s', percentage: 25, color: 'bg-green-500' },
                { range: '1-2s', percentage: 45, color: 'bg-blue-500' },
                { range: '2-3s', percentage: 20, color: 'bg-yellow-500' },
                { range: '> 3s', percentage: 10, color: 'bg-red-500' }
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600 w-12">{item.range}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`${item.color} h-2 rounded-full transition-all duration-1000`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-8 text-right">{item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Task Categories Pie Chart */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Task Categories</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Distribution</span>
              </div>
            </div>
            <div className="relative h-40 mb-4">
              {/* Simulated Pie Chart */}
              <svg className="w-full h-full" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r="80" fill="#e5e7eb"/>
                <path d="M 100 100 L 100 20 A 80 80 0 0 1 180 100 Z" fill="#3b82f6"/>
                <path d="M 100 100 L 180 100 A 80 80 0 0 1 160 160 Z" fill="#10b981"/>
                <path d="M 100 100 L 160 160 A 80 80 0 0 1 100 180 Z" fill="#f59e0b"/>
                <path d="M 100 100 L 100 180 A 80 80 0 0 1 40 160 Z" fill="#ef4444"/>
                <path d="M 100 100 L 40 160 A 80 80 0 0 1 20 100 Z" fill="#8b5cf6"/>
                <path d="M 100 100 L 20 100 A 80 80 0 0 1 40 40 Z" fill="#06b6d4"/>
                <circle cx="100" cy="100" r="40" fill="white"/>
              </svg>
            </div>
            <div className="space-y-2">
              {[
                { label: 'Document Processing', color: 'bg-blue-500', percentage: 30 },
                { label: 'Customer Support', color: 'bg-green-500', percentage: 25 },
                { label: 'Analytics', color: 'bg-yellow-500', percentage: 20 },
                { label: 'Compliance', color: 'bg-red-500', percentage: 15 },
                { label: 'Other', color: 'bg-purple-500', percentage: 10 }
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className={`w-3 h-3 ${item.color} rounded-full`}></div>
                  <span className="text-sm text-gray-600 flex-1">{item.label}</span>
                  <span className="text-sm font-semibold text-gray-900">{item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* System Health Status */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">All Systems OK</span>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { name: 'CPU Usage', value: 45, status: 'good', color: 'bg-green-500' },
                { name: 'Memory', value: 62, status: 'good', color: 'bg-green-500' },
                { name: 'Storage', value: 78, status: 'warning', color: 'bg-yellow-500' },
                { name: 'Network', value: 23, status: 'good', color: 'bg-green-500' }
              ].map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{item.name}</span>
                    <span className="text-sm font-semibold text-gray-900">{item.value}%</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className={`${item.color} h-2 rounded-full transition-all duration-1000`}
                      style={{ width: `${item.value}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Uptime</span>
                <span className="font-semibold text-gray-900">99.9%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Activity Feed */}
      <div className="relative mb-16">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Real-time Activity Feed</h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-600">Live</span>
            </div>
          </div>
          <div className="space-y-4 max-h-64 overflow-y-auto">
            {[
              { agent: 'OCR Agent', action: 'Processed document', time: '2 min ago', status: 'success' },
              { agent: 'Customer Support', action: 'Resolved ticket #1234', time: '3 min ago', status: 'success' },
              { agent: 'Fraud Detection', action: 'Flagged suspicious transaction', time: '5 min ago', status: 'warning' },
              { agent: 'Analytics Dashboard', action: 'Generated weekly report', time: '8 min ago', status: 'success' },
              { agent: 'Claims Processing', action: 'Approved claim #5678', time: '12 min ago', status: 'success' },
              { agent: 'Risk Assessment', action: 'Updated risk score', time: '15 min ago', status: 'success' }
            ].map((activity, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-2 h-2 rounded-full ${
                  activity.status === 'success' ? 'bg-green-500' : 
                  activity.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{activity.agent}</span>
                    <span className="text-sm text-gray-600">{activity.action}</span>
                  </div>
                  <div className="text-xs text-gray-500">{activity.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions - Full Width Layout */}
      <div className="relative">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-4">
            Quick Actions & System Status
          </h2>
          <p className="text-gray-600 text-lg">Streamline your AI operations and monitor system health</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          <button className="group relative p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
            <div className="relative text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-white text-lg">🚀</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">Deploy Agent</p>
              <p className="text-xs text-gray-600 mt-1">Launch new AI</p>
            </div>
          </button>
          
          <button className="group relative p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
            <div className="relative text-center">
              <div className="relative w-12 h-12 mx-auto mb-3">
                <div className="absolute inset-0 rounded-full border-3 border-gray-200"></div>
                <div className="absolute inset-0 rounded-full border-3 border-green-500" style={{ clipPath: 'polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%)' }}></div>
                <div className="absolute inset-0 rounded-full border-3 border-green-600" style={{ clipPath: 'polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%)' }}></div>
                <div className="absolute inset-0 rounded-full border-3 border-green-400" style={{ clipPath: 'polygon(50% 50%, 50% 100%, 0% 100%, 0% 50%)' }}></div>
                <div className="absolute inset-1 bg-white rounded-full flex items-center justify-center">
                  <span className="text-green-600 text-sm font-bold">87%</span>
                </div>
              </div>
              <p className="text-sm font-semibold text-gray-900">Analytics</p>
              <p className="text-xs text-gray-600 mt-1">87% Complete</p>
            </div>
          </button>
          
          <button className="group relative p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
            <div className="relative text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-white text-lg">⚙️</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">Configure</p>
              <p className="text-xs text-gray-600 mt-1">System settings</p>
            </div>
          </button>

          <button className="group relative p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
            <div className="relative text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-white text-lg">🔧</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">Maintenance</p>
              <p className="text-xs text-gray-600 mt-1">System health</p>
            </div>
          </button>

          <button className="group relative p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
            <div className="relative text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-white text-lg">🔒</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">Security</p>
              <p className="text-xs text-gray-600 mt-1">Access control</p>
            </div>
          </button>

          <button className="group relative p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-pink-500/20 to-rose-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
            <div className="relative text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-white text-lg">📊</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">Monitor</p>
              <p className="text-xs text-gray-600 mt-1">Real-time data</p>
            </div>
          </button>

          <button className="group relative p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
            <div className="relative text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-white text-lg">⚡</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">Performance</p>
              <p className="text-xs text-gray-600 mt-1">Optimize speed</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};